import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import {
  useDocumentQuery,
  usePaperFiguresQuery,
  usePaperQuery,
} from "@/features/citations/api/queries";
import { formatAuthors } from "@/features/citations/utils/formatAuthors";
import { useDrawerA11y } from "@/hooks/useDrawerA11y";
import { cn } from "@/utils/cn";
import { useQuery } from "@tanstack/react-query";
import { CircleAlert, ExternalLink, Loader2, X } from "lucide-react";
import { useEffect, useMemo, useRef } from "react";
import { Link } from "react-router-dom";
import { fetchGrepLineNumbers } from "../api/playground";
import type { PaperDetailTarget } from "../types";

const READER_ROUTE: Record<string, string> = {
  papers: "papers",
  fda: "fda",
  clinicaltrials: "clinicaltrials",
  patents: "patents",
};

/**
 * Slide-over showing a document exactly as the API retrieves it: metadata,
 * extracted figures (papers only), and every content block with its L-number.
 * When opened from a grep result, the matching blocks are highlighted and
 * scrolled to.
 */
export const PaperDetailPanel = ({
  target,
  onClose,
}: {
  target: PaperDetailTarget | null;
  onClose: () => void;
}) => {
  const isOpen = target !== null;
  const docId = target?.docId;
  const source = target?.source ?? "papers";
  const isPaper = source === "papers";
  const grepPattern = isPaper ? target?.grepPattern : undefined;
  const providedHighlightNums = isPaper ? target?.grepLineNumbers : undefined;
  const hasProvidedHighlights = (providedHighlightNums?.length ?? 0) > 0;
  const panelRef = useRef<HTMLElement>(null);
  const bodyRef = useRef<HTMLDivElement>(null);

  const paperQuery = usePaperQuery(isPaper ? docId : undefined);
  // Non-paper sources (FDA, trials, patents) go through the generic document
  // viewer API; the query is disabled for papers.
  const documentQuery = useDocumentQuery(source, isPaper ? undefined : docId, []);
  const { data, isPending, error } = isPaper ? paperQuery : documentQuery;
  const { data: figures = [] } = usePaperFiguresQuery(isPaper ? docId : undefined);

  const { data: highlightNums, isFetching: highlightsLoading } = useQuery({
    queryKey: [
      "playground",
      "grep-lines",
      docId,
      grepPattern,
      target?.grepIgnoreCase,
    ],
    queryFn: () =>
      fetchGrepLineNumbers(docId!, grepPattern!, target?.grepIgnoreCase),
    enabled: !!docId && !!grepPattern && !hasProvidedHighlights,
    staleTime: Infinity,
  });
  const highlights = useMemo(
    () =>
      new Set(
        hasProvidedHighlights ? providedHighlightNums : (highlightNums ?? []),
      ),
    [hasProvidedHighlights, highlightNums, providedHighlightNums],
  );

  useDrawerA11y({ isOpen, onClose, containerRef: panelRef, enabled: isOpen });

  // Jump to the first highlighted block once content and matches are in.
  useEffect(() => {
    if (!data || highlights.size === 0 || !bodyRef.current) return;
    const first = Math.min(...highlights);
    const el = bodyRef.current.querySelector(`[data-line="${first}"]`);
    el?.scrollIntoView({ block: "center" });
  }, [data, highlights]);

  if (!isOpen) return null;

  const meta = data?.meta;
  const readerHash =
    highlights.size > 0
      ? `#L${[...highlights].sort((a, b) => a - b).join(",L")}`
      : "";

  return (
    <div className="fixed inset-0 z-[60]">
      <div
        aria-hidden
        className="absolute inset-0 bg-zinc-900/40"
        onClick={onClose}
      />
      <aside
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={`Document ${docId}`}
        tabIndex={-1}
        className="absolute bottom-0 right-0 top-0 flex w-[min(44rem,94vw)] flex-col bg-white shadow-[-8px_0_32px_rgba(0,0,0,0.15)]"
      >
        <header className="shrink-0 border-b border-zinc-200 px-5 py-3.5">
          <div className="flex items-center gap-2">
            {(meta?.source || !isPaper) && (
              <Badge variant="blue" className="text-3xs uppercase tracking-wide">
                {meta?.source || source}
              </Badge>
            )}
            <span className="font-mono text-2xs text-zinc-400">{docId}</span>
            {grepPattern && (
              <span className="flex items-center gap-1 text-2xs text-amber-600">
                {highlightsLoading ? (
                  <Loader2 className="h-3 w-3 animate-spin" aria-hidden />
                ) : (
                  `${highlights.size} matching block${highlights.size === 1 ? "" : "s"}`
                )}
              </span>
            )}
            <div className="ml-auto flex items-center gap-1">
              <Link
                to={`/citations/${READER_ROUTE[source]}/${docId}${readerHash}`}
                target="_blank"
                rel="noreferrer"
                title="Open in full reader"
                className="inline-flex h-7 w-7 items-center justify-center rounded-[3px] text-zinc-500 transition-colors hover:bg-zinc-100 hover:text-zinc-900"
              >
                <ExternalLink className="h-3.5 w-3.5" aria-hidden />
                <span className="sr-only">Open in full reader</span>
              </Link>
              <Button
                variant="ghost"
                size="icon-sm"
                aria-label="Close document panel"
                onClick={onClose}
              >
                <X className="h-4 w-4" aria-hidden />
              </Button>
            </div>
          </div>
          {meta?.title && (
            <h2 className="mt-1.5 text-base leading-7 text-zinc-900">
              {meta.title}
            </h2>
          )}
          {(meta?.authors || meta?.pub_year || meta?.journal) && (
            <p className="mt-0.5 truncate text-xs text-zinc-500">
              {[formatAuthors(meta?.authors), meta?.journal, meta?.pub_year]
                .filter(Boolean)
                .join(" · ")}
            </p>
          )}
        </header>

        <div ref={bodyRef} className="min-h-0 flex-1 overflow-y-auto">
          {isPending ? (
            <div className="flex items-center justify-center gap-2 py-20 text-zinc-400">
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
              <span className="text-sm">Fetching content blocks…</span>
            </div>
          ) : error ? (
            <div className="flex flex-col items-center gap-2 py-20 text-center">
              <CircleAlert className="h-5 w-5 text-red-500" aria-hidden />
              <p className="text-sm text-red-700" role="alert">
                {error instanceof Error ? error.message : "Failed to load document"}
              </p>
            </div>
          ) : (
            <>
              {figures.length > 0 && (
                <div className="border-b border-zinc-100 px-5 py-3">
                  <p className="mb-2 text-2xs font-medium uppercase tracking-wide text-zinc-400">
                    Extracted figures ({figures.length})
                  </p>
                  <div className="flex gap-2 overflow-x-auto pb-1">
                    {figures.map((fig) => (
                      <img
                        key={fig.filename}
                        src={fig.url}
                        alt={
                          fig.figure_num != null
                            ? `Figure ${fig.figure_num}`
                            : fig.filename
                        }
                        loading="lazy"
                        className="h-20 shrink-0 rounded border border-zinc-200 object-contain"
                      />
                    ))}
                  </div>
                </div>
              )}
              <div className="px-5 py-4">
                {(data?.lines ?? []).map((line) => {
                  const highlighted = highlights.has(line.num);
                  return (
                    <div
                      key={line.num}
                      data-line={line.num}
                      className={cn(
                        "flex gap-3 rounded px-1.5 py-1",
                        highlighted && "bg-amber-50 ring-1 ring-inset ring-amber-200",
                      )}
                    >
                      <span
                        className={cn(
                          "w-11 shrink-0 select-none pt-px text-right font-mono text-3xs tabular-nums",
                          highlighted ? "font-semibold text-amber-600" : "text-zinc-300",
                        )}
                      >
                        L{line.num}
                      </span>
                      <span className="min-w-0 text-sm leading-relaxed text-zinc-800">
                        {line.text}
                      </span>
                    </div>
                  );
                })}
                {(data?.lines ?? []).length === 0 && (
                  <p className="py-10 text-center text-sm text-zinc-500">
                    No content blocks available for this document.
                  </p>
                )}
              </div>
            </>
          )}
        </div>
      </aside>
    </div>
  );
};
